import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { NavigatorStore } from '../stores';
import Snowfall from 'react-snowfall';
import { ITopItem } from '../../../interfaces/ITopItem';
import Confetti from 'confetti-react';
import { IAButton } from '../../button/Button';
import { IAIcon } from '../../icon/Icon';
import { Helper } from '../../../Helper';
import { Environment } from '../../../services/Environment';
import { LocalizationService } from '../../../services/LocalizationService';
import { createImageConfettiDrawShape, linearTween, preloadConfettiImages } from '../../global/ImageConfettiHelper';

declare global {
  interface Window { snowStorm: any }
}

// The only two things persisted to localStorage for celebrations - shared across all subTypes
// since only one celebration is ever configured/relevant at a time in practice. Each is a
// celebrationVersion string compared against the CURRENTLY configured celebrationVersion, so a
// celebration automatically reactivates (and its infobox reappears) once the admin changes its
// settings, with no separate "reactivate" step needed.
const DEACTIVATED_VERSION_KEY = "DeactivatedVersion";
const INFOBOX_DISMISSED_VERSION_KEY = "InfoboxDismissedVersion";

// Looks up the currently configured celebrationVersion for a given subtype.
function resolveCelebrationVersion(navigatorStore: NavigatorStore, subType: "snow" | "confetti" | "imageconfetti"): string | undefined {
  return navigatorStore.navigator?.smartmenu?.elements?.find((element: ITopItem) => element.config?.subType === subType)?.config?.celebrationVersion;
}

// Exported so Smartmenu.tsx's hover popover (activate/deactivate button) reads/writes the exact
// same localStorage key this component's own render() uses.
export function isCelebrationActive(navigatorStore: NavigatorStore, subType: "snow" | "confetti" | "imageconfetti"): boolean {
  const deactivatedVersion = localStorage.getItem(DEACTIVATED_VERSION_KEY);
  return deactivatedVersion == undefined || deactivatedVersion !== (resolveCelebrationVersion(navigatorStore, subType) || "");
}

export function toggleCelebrationActive(navigatorStore: NavigatorStore, subType: "snow" | "confetti" | "imageconfetti"): void {
  if (isCelebrationActive(navigatorStore, subType)) {
    Helper.setLocalStorage(DEACTIVATED_VERSION_KEY, resolveCelebrationVersion(navigatorStore, subType) || "");
  } else {
    localStorage.removeItem(DEACTIVATED_VERSION_KEY);
  }
  // localStorage writes alone don't trigger a MobX re-render - bump an observable so the
  // (separately mounted) IAConfetti instance picks up the change without a page reload.
  navigatorStore.celebrationToggleVersion++;
}

// Images uploaded to SharePoint (via the Navigator's own media selector, into the
// IANavigatorImages list) aren't publicly reachable - they need to go through the image API
// (which attaches an auth token) to actually load. Anything else (e.g. the built-in
// CDN-hosted templates) is already public and is used as-is.
function resolveConfettiImageSrc(navigatorStore: NavigatorStore, imageUrl: string): string {
  if (imageUrl && imageUrl.indexOf("IntraActive-Navigator") !== -1) {
    return Helper.getImageUrl(
      Environment.getEnvironmentForImageAPI(navigatorStore.environment),
      navigatorStore.tenant?.replace(".sharepoint.com", ""),
      navigatorStore.token,
      imageUrl
    );
  }
  return imageUrl;
}

export interface IProps {
  navigatorStore?: NavigatorStore;
}

export interface IState {
  confettiWrapper: JSX.Element[];
  eggCount: number;
  eggIndex: number;
  userInteraction?: boolean;
  imageConfettiReady: boolean;
  previewImageConfettiReady: boolean;
  // Only the preview infobox needs in-memory dismiss tracking - the real snow/confetti/
  // imageconfetti infoboxes are dismissed persistently (see isInfoboxDismissed/dismissInfobox).
  dismissedInfoboxes: { preview: boolean };
  // Session-only ("this page view" only) suppression once a celebration's duration timer
  // elapses - nothing about duration is persisted to localStorage, so a fresh page load
  // always gets a full duration again (see syncCelebrationDurations/startCelebrationDurationTimer).
  durationElapsed: { snow: boolean; confetti: boolean; imageconfetti: boolean };
  // Confetti/imageconfetti only: duration elapsed but still in the "fallout" period, so
  // recycling is turned off (already-airborne pieces keep falling) without unmounting yet.
  spawningStopped: { confetti: boolean; imageconfetti: boolean };
  // Starting the (heavier) imageconfetti animation while the host page is still loading its
  // webparts makes it compete with that work for the main thread and visibly stutter/catch up
  // once loading settles - so its mount is held back until the page has finished loading.
  pageLoaded: boolean;
}

@inject('navigatorStore')
@observer
export class IAConfetti extends React.Component<IProps, IState> {
  // navigatorStore is the same mutated instance across renders, so prevProps can't be used
  // to detect a new preview request; track the last one we've started preloading instead.
  private lastHandledPreviewKey: string;
  // Guards ensureImageConfettiPreloaded against starting the same preload more than once while
  // its promise is still pending (componentDidUpdate calls it on every update).
  private imageConfettiPreloadStarted: boolean = false;
  // Incremented each time a subtype's duration timer (re)starts, so stale setTimeout callbacks
  // from a PREVIOUS activation period (e.g. one that was deactivated before its timer fired) can
  // recognize they've been superseded and no-op instead of wrongly suppressing a later, freshly
  // (re)activated run.
  private durationGeneration: { snow: number; confetti: number; imageconfetti: number } = { snow: 0, confetti: 0, imageconfetti: 0 };
  // Tracks each subtype's active/shown state as of the last sync, so syncCelebrationDurations can
  // detect an inactive -> active transition (page load while active, or manual reactivation) and
  // only then (re)start its duration countdown - never while still inactive/deactivated.
  private wasActive: { snow: boolean; confetti: boolean; imageconfetti: boolean } = { snow: false, confetti: false, imageconfetti: false };

  constructor(props) {
    super(props);
    this.state = {
      confettiWrapper: [],
      eggCount: 0,
      eggIndex: 0,
      userInteraction: false,
      imageConfettiReady: false,
      previewImageConfettiReady: false,
      dismissedInfoboxes: { preview: false },
      durationElapsed: { snow: false, confetti: false, imageconfetti: false },
      spawningStopped: { confetti: false, imageconfetti: false },
      pageLoaded: document.readyState === "complete"
    }
  }

  private handlePageLoad = (): void => {
    this.setState({ pageLoaded: true });
  }

  public componentDidUpdate(): void {
    // componentDidMount can run before navigatorStore.navigator has finished loading
    // asynchronously - retry here on every update so imageconfetti's preload isn't skipped forever.
    this.ensureImageConfettiPreloaded();
    this.syncCelebrationDurations();
    const preview = this.props.navigatorStore.celebrationPreview;
    if (preview && preview.key !== this.lastHandledPreviewKey) {
      this.lastHandledPreviewKey = preview.key;
      this.setState({ dismissedInfoboxes: { ...this.state.dismissedInfoboxes, preview: false } });
      if (preview.subType === "imageconfetti" && preview.imageUrls?.length > 0) {
        this.setState({ previewImageConfettiReady: false });
        preloadConfettiImages(preview.imageUrls, (imageUrl) => resolveConfettiImageSrc(this.props.navigatorStore, imageUrl)).then(() => {
          if (this.props.navigatorStore.celebrationPreview?.key === preview.key) {
            this.setState({ previewImageConfettiReady: true });
          }
        });
      }
    }
  }

  // Looks up the currently configured celebrationVersion for a given subtype, so it can be
  // stamped into localStorage at the moment the end user deactivates that celebration.
  private getCelebrationVersion(subType: "snow" | "confetti" | "imageconfetti"): string | undefined {
    return this.props.navigatorStore.navigator?.smartmenu?.elements?.find((element: ITopItem) => element.config?.subType === subType)?.config?.celebrationVersion;
  }

  // Starts (or restarts) each active, duration-configured celebration's countdown exactly when it
  // becomes active - NOT at component mount - so a celebration that's deactivated and later
  // reactivated (e.g. via Smartmenu's popover) always gets its full configured duration from that
  // moment, instead of disappearing early because a stale mount-time timer kept counting down
  // the whole time it was off.
  private syncCelebrationDurations(): void {
    (['snow', 'confetti', 'imageconfetti'] as const).forEach((subType) => {
      const element = this.props.navigatorStore.navigator?.smartmenu?.elements?.find((e: ITopItem) => e.config?.subType === subType);
      const isActiveNow = !!element && isCelebrationActive(this.props.navigatorStore, subType);
      if (isActiveNow && !this.wasActive[subType]) {
        this.durationGeneration[subType]++;
        this.setState({
          durationElapsed: { ...this.state.durationElapsed, [subType]: false },
          spawningStopped: subType === "snow" ? this.state.spawningStopped : { ...this.state.spawningStopped, [subType]: false }
        });
        this.startCelebrationDurationTimer(element.config.durationInSeconds, subType, this.durationGeneration[subType]);
      }
      this.wasActive[subType] = isActiveNow;
    });
  }

  // If a duration is configured for a celebration type, stops it automatically that many seconds
  // after it actually became active (no duration = runs indefinitely). Purely in-memory/per-page-
  // view - nothing about duration is persisted to localStorage. Confetti/imageconfetti first stop
  // spawning new pieces (IState.spawningStopped, recycling off so already-airborne pieces keep
  // falling) and only once the fallout period also elapses are fully suppressed
  // (IState.durationElapsed). Snow has no recycle-equivalent, so it just stops immediately once
  // its duration elapses. Each callback checks durationGeneration before applying, so it no-ops
  // if this activation period has since been superseded by a later one (see syncCelebrationDurations).
  private startCelebrationDurationTimer(durationInSeconds: number | undefined, subType: "snow" | "confetti" | "imageconfetti", generation: number): void {
    if (!durationInSeconds) {
      return;
    }
    const falloutMs = subType === "imageconfetti" ? 9000 : subType === "confetti" ? 4000 : 0;
    if (subType === "confetti" || subType === "imageconfetti") {
      setTimeout(() => {
        if (this.durationGeneration[subType] !== generation) {
          return;
        }
        this.setState({ spawningStopped: { ...this.state.spawningStopped, [subType]: true } });
      }, durationInSeconds * 1000);
    }
    setTimeout(() => {
      if (this.durationGeneration[subType] !== generation) {
        return;
      }
      this.setState({ durationElapsed: { ...this.state.durationElapsed, [subType]: true } });
    }, durationInSeconds * 1000 + falloutMs);
  }

  public componentDidMount(): void {
    this.syncCelebrationDurations();
    this.ensureImageConfettiPreloaded();
    if (!this.state.pageLoaded) {
      window.addEventListener("load", this.handlePageLoad);
    }
  }

  public componentWillUnmount(): void {
    window.removeEventListener("load", this.handlePageLoad);
  }

  // Preloads the live (non-preview) imageconfetti element's images, idempotently - safe to call
  // repeatedly (e.g. from componentDidUpdate) since it no-ops once already ready or in progress.
  private ensureImageConfettiPreloaded(): void {
    if (this.state.imageConfettiReady || this.imageConfettiPreloadStarted) {
      return;
    }
    const imageConfettiElement = this.props.navigatorStore.navigator?.smartmenu?.elements?.find((element: ITopItem) => element.config?.subType === "imageconfetti");
    const configuredImageUrls = imageConfettiElement ? (imageConfettiElement.config.imageUrls && imageConfettiElement.config.imageUrls.length > 0 ? imageConfettiElement.config.imageUrls : imageConfettiElement.config.imageUrl ? [imageConfettiElement.config.imageUrl] : []) : [];
    if (configuredImageUrls.length > 0) {
      this.imageConfettiPreloadStarted = true;
      preloadConfettiImages(configuredImageUrls, (imageUrl) => resolveConfettiImageSrc(this.props.navigatorStore, imageUrl)).then(() => {
        this.setState({ imageConfettiReady: true });
      });
    }
  }

  // Records which version of the celebration was active when the end user deactivated it.
  private deactivateCelebration(subType: "snow" | "confetti" | "imageconfetti"): void {
    Helper.setLocalStorage(DEACTIVATED_VERSION_KEY, this.getCelebrationVersion(subType) || "");
  }

  // An infobox stays dismissed only for the celebrationVersion it was dismissed at - if the
  // admin changes the celebration afterwards, the (new) infobox is shown again.
  private isInfoboxDismissed(currentVersion: string | undefined): boolean {
    const dismissedVersion = localStorage.getItem(INFOBOX_DISMISSED_VERSION_KEY);
    return dismissedVersion != undefined && dismissedVersion === (currentVersion || "");
  }

  private dismissInfobox(subType: "snow" | "confetti" | "imageconfetti"): void {
    Helper.setLocalStorage(INFOBOX_DISMISSED_VERSION_KEY, this.getCelebrationVersion(subType) || "");
    this.forceUpdate();
  }

  // Renders the small "why is this happening" card shown in the bottom-right corner
  // while a celebration (real or previewed) is active and its infobox toggle is enabled.
  private renderConfettiInfobox(headline: string, message: string, backgroundColor: string, textColor: string, onDeactivate: () => void, onDismiss: () => void): JSX.Element {
    const resolvedBackgroundColor = backgroundColor || "#ffffff";
    const resolvedTextColor = textColor || "#333333";
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 20,
          left: 20,
          width: 420,
          backgroundColor: resolvedBackgroundColor,
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
          padding: 16,
          pointerEvents: "auto",
          zIndex: 4000001
        }}>
        <IAIcon
          title={"Close"}
          size={14}
          color={resolvedTextColor}
          onClick={onDismiss}
          style={{
            float: "right",
            marginLeft: 8,
            cursor: "pointer"
          }}
        />
        <div style={{ fontSize: 16, fontWeight: 600, color: resolvedTextColor, marginBottom: 8 }}>
          {headline || "Skal vi fejre?"}
        </div>
        <div style={{ fontSize: 14, color: resolvedTextColor, marginBottom: 12 }}>
          {message || "Du kan deaktivere konfetti ved at klikke på knappen herunder"}
        </div>
        <div style={{ fontSize: 12, opacity: 0.8, color: resolvedTextColor, marginBottom: 12 }}>
          {new LocalizationService().strings.Navigator_ConfettiInfobox_StandardText}
        </div>
        <IAButton
          label={new LocalizationService().strings.Navigator_ConfettiPopupStartConfetti || "On/Off"}
          buttonColor={resolvedTextColor}
          textColor={Helper.getTextColorBasedOnBackgroundColorDarkness(resolvedTextColor, "#ffffff", "#000000")}
          showSpinner={false}
          fontSize={14}
          onClick={onDeactivate}
          style={{ borderRadius: 5 }}
        />
      </div>
    );
  }

  render() {
    const navigatorElements = this.props.navigatorStore.navigator?.smartmenu?.elements;
    const canShowCelebrations = document.getElementById("navigatorClassic") == undefined && !this.props.navigatorStore.isInEditMode && !this.props.navigatorStore.isMobile;
    // Active by default - a missing/unrecognized DeactivatedVersion is never treated as "off",
    // and a celebration automatically reactivates once the admin changes it (new
    // celebrationVersion no longer matches the stored DeactivatedVersion).
    const deactivatedVersion = localStorage.getItem(DEACTIVATED_VERSION_KEY);

    let showSnow = false;
    let snowInfobox: { show: boolean, headline?: string, message?: string, backgroundColor?: string, textColor?: string, version?: string } = { show: false };
    const snowElement = navigatorElements?.find((element: ITopItem) => element.config?.subType === "snow");
    const snowVersion = snowElement?.config?.celebrationVersion;
    // Also suppressed for the rest of this page view once its duration timer elapses, without
    // that counting as a deactivation (see syncCelebrationDurations/startCelebrationDurationTimer).
    const snowIsActive = (deactivatedVersion == undefined || deactivatedVersion !== (snowVersion || "")) && !this.state.durationElapsed.snow;
    if (snowElement && snowElement.type === "celebration" && snowIsActive && canShowCelebrations) {
      showSnow = true;
      snowInfobox = { show: !!snowElement.config.showConfettiInfobox, headline: snowElement.config.confettiInfoboxHeadline, message: snowElement.config.confettiInfoboxMessage, backgroundColor: snowElement.config.confettiInfoboxBackgroundColor, textColor: snowElement.config.confettiInfoboxTextColor, version: snowVersion };
    }

    let showConfetti = false;
    let confettiInfobox: { show: boolean, headline?: string, message?: string, backgroundColor?: string, textColor?: string, version?: string } = { show: false };
    const confettiElement = navigatorElements?.find((element: ITopItem) => element.config?.subType === "confetti");
    const confettiVersion = confettiElement?.config?.celebrationVersion;
    const confettiIsActive = (deactivatedVersion == undefined || deactivatedVersion !== (confettiVersion || "")) && !this.state.durationElapsed.confetti;
    const confettiRecycle = !this.state.spawningStopped.confetti;
    if (confettiElement && confettiElement.type === "celebration" && confettiIsActive && canShowCelebrations) {
      showConfetti = true;
      confettiInfobox = { show: !!confettiElement.config.showConfettiInfobox, headline: confettiElement.config.confettiInfoboxHeadline, message: confettiElement.config.confettiInfoboxMessage, backgroundColor: confettiElement.config.confettiInfoboxBackgroundColor, textColor: confettiElement.config.confettiInfoboxTextColor, version: confettiVersion };
    }

    let showImageConfetti = false;
    let imageConfettiUrls: string[] = [];
    let imageConfettiInfobox: { show: boolean, headline?: string, message?: string, backgroundColor?: string, textColor?: string, version?: string } = { show: false };
    const imageConfettiElement = navigatorElements?.find((element: ITopItem) => element.config?.subType === "imageconfetti");
    const imageConfettiVersion = imageConfettiElement?.config?.celebrationVersion;
    const imageConfettiIsActive = (deactivatedVersion == undefined || deactivatedVersion !== (imageConfettiVersion || "")) && !this.state.durationElapsed.imageconfetti;
    const imageConfettiRecycle = !this.state.spawningStopped.imageconfetti;
    if (imageConfettiElement && imageConfettiElement.type === "celebration" && imageConfettiIsActive && canShowCelebrations) {
      const configuredImageUrls = imageConfettiElement.config.imageUrls && imageConfettiElement.config.imageUrls.length > 0 ? imageConfettiElement.config.imageUrls : imageConfettiElement.config.imageUrl ? [imageConfettiElement.config.imageUrl] : [];
      if (configuredImageUrls.length > 0) {
        showImageConfetti = true;
        imageConfettiUrls = configuredImageUrls;
        imageConfettiInfobox = { show: !!imageConfettiElement.config.showConfettiInfobox, headline: imageConfettiElement.config.confettiInfoboxHeadline, message: imageConfettiElement.config.confettiInfoboxMessage, backgroundColor: imageConfettiElement.config.confettiInfoboxBackgroundColor, textColor: imageConfettiElement.config.confettiInfoboxTextColor, version: imageConfettiVersion };
      }
    }

    const snowInfoboxDismissed = this.isInfoboxDismissed(snowInfobox.version);
    const confettiInfoboxDismissed = this.isInfoboxDismissed(confettiInfobox.version);
    const imageConfettiInfoboxDismissed = this.isInfoboxDismissed(imageConfettiInfobox.version);
    // Admins previewing targeted content shouldn't also be shown the (unrelated) celebration infobox card.
    const suppressInfoboxForTargetedPreview = this.props.navigatorStore.targetedPreviewIds?.length > 0;
    return (
      // data attribute only - reads celebrationToggleVersion so MobX re-renders this component
      // when Smartmenu's hover popover toggles a celebration (plain localStorage writes aren't observable).
      <div data-celebration-toggle-version={this.props.navigatorStore.celebrationToggleVersion}>
        <div
          id="Snow"
          style={{
            display: snowIsActive ? "block" : "none",
            position: 'fixed',
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 4000000,
            pointerEvents: "none",
            background: undefined
          }}>
          {showSnow &&
            <Snowfall
              color="white"
            />
          }
        </div>

        <div
          id="Confetti"
          style={{
            display: confettiIsActive ? "block" : "none",
            position: 'fixed',
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 4000000,
            pointerEvents: "none",
            background: undefined
          }}>
          {showConfetti &&
            <Confetti
              width={screen.width}
              height={screen.height}
              gravity={0.03}
              numberOfPieces={40}
              recycle={confettiRecycle}
              confettiSource={{ x: 0, y: -20, w: screen.width, h: 0 }}
            />
          }
        </div>
        <div
          id="ImageConfetti"
          style={{
            display: imageConfettiIsActive ? "block" : "none",
            position: 'fixed',
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 4000000,
            pointerEvents: "none",
            background: undefined
          }}>
          {showImageConfetti && imageConfettiUrls.length > 0 && this.state.imageConfettiReady && this.state.pageLoaded &&
            <Confetti
              width={screen.width}
              height={screen.height}
              gravity={0.02}
              // At this slow gravity a piece takes ~7-9s to fall, but the population
              // should reach full density almost immediately and just hold steady
              // until pieces start exiting - a long ramp visibly grows in density for
              // several seconds first, which is what read as "dense, then thins".
              tweenDuration={1200}
              tweenFunction={linearTween}
              // Pieces are drawn much larger than classic confetti (3.5x radius), so
              // fewer are needed to look "full" - 100 of them was overly dense.
              numberOfPieces={45}
              recycle={imageConfettiRecycle}
              // Kept strictly above the visible screen (h: 0, same as classic confetti) per
              // request - a wider band would smooth out the residual "waves" (measured via
              // simulation) but pieces would then visibly spawn already inside the screen.
              confettiSource={{ x: 0, y: -20, w: screen.width, h: 0 }}
              drawShape={createImageConfettiDrawShape(imageConfettiUrls, (imageUrl) => resolveConfettiImageSrc(this.props.navigatorStore, imageUrl))}
            />
          }
        </div>
        <div
          id="CelebrationPreview"
          style={{
            display: this.props.navigatorStore.celebrationPreview ? "block" : "none",
            position: 'fixed',
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 4000000,
            pointerEvents: "none",
            background: undefined
          }}>
          {this.props.navigatorStore.celebrationPreview?.subType === "snow" &&
            <Snowfall color="white" />
          }
          {this.props.navigatorStore.celebrationPreview?.subType === "confetti" &&
            <Confetti
              width={screen.width}
              height={screen.height}
              gravity={0.03}
              numberOfPieces={40}
              recycle={!this.props.navigatorStore.celebrationPreview.stopSpawning}
              confettiSource={{ x: 0, y: -20, w: screen.width, h: 0 }}
            />
          }
          {this.props.navigatorStore.celebrationPreview?.subType === "imageconfetti" && this.props.navigatorStore.celebrationPreview.imageUrls?.length > 0 && this.state.previewImageConfettiReady &&
            <Confetti
              width={screen.width}
              height={screen.height}
              gravity={0.02}
              tweenDuration={1200}
              tweenFunction={linearTween}
              numberOfPieces={45}
              recycle={!this.props.navigatorStore.celebrationPreview.stopSpawning}
              confettiSource={{ x: 0, y: -20, w: screen.width, h: 0 }}
              drawShape={createImageConfettiDrawShape(this.props.navigatorStore.celebrationPreview.imageUrls, (imageUrl) => resolveConfettiImageSrc(this.props.navigatorStore, imageUrl))}
            />
          }
        </div>
        {showSnow && snowInfobox.show && !snowInfoboxDismissed && !suppressInfoboxForTargetedPreview &&
          this.renderConfettiInfobox(snowInfobox.headline, snowInfobox.message, snowInfobox.backgroundColor, snowInfobox.textColor, () => {
            this.deactivateCelebration("snow");
            this.forceUpdate();
          }, () => this.dismissInfobox("snow"))
        }
        {showConfetti && confettiInfobox.show && !confettiInfoboxDismissed && !suppressInfoboxForTargetedPreview &&
          this.renderConfettiInfobox(confettiInfobox.headline, confettiInfobox.message, confettiInfobox.backgroundColor, confettiInfobox.textColor, () => {
            this.deactivateCelebration("confetti");
            this.forceUpdate();
          }, () => this.dismissInfobox("confetti"))
        }
        {showImageConfetti && imageConfettiInfobox.show && !imageConfettiInfoboxDismissed && !suppressInfoboxForTargetedPreview &&
          this.renderConfettiInfobox(imageConfettiInfobox.headline, imageConfettiInfobox.message, imageConfettiInfobox.backgroundColor, imageConfettiInfobox.textColor, () => {
            this.deactivateCelebration("imageconfetti");
            this.forceUpdate();
          }, () => this.dismissInfobox("imageconfetti"))
        }
        {this.props.navigatorStore.celebrationPreview?.showConfettiInfobox && !this.state.dismissedInfoboxes.preview && !suppressInfoboxForTargetedPreview &&
          this.renderConfettiInfobox(
            this.props.navigatorStore.celebrationPreview.confettiInfoboxHeadline,
            this.props.navigatorStore.celebrationPreview.confettiInfoboxMessage,
            this.props.navigatorStore.celebrationPreview.confettiInfoboxBackgroundColor,
            this.props.navigatorStore.celebrationPreview.confettiInfoboxTextColor,
            () => this.props.navigatorStore.stopCelebrationPreview(),
            () => this.setState({ dismissedInfoboxes: { ...this.state.dismissedInfoboxes, preview: true } })
          )
        }
        <div
          id="ConfettiWrapper"
          style={{
            display: "block",
            position: 'fixed',
            top: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 4000000,
            pointerEvents: "none",
            background: undefined
          }}>
          {
            this.state.confettiWrapper
          }
        </div>
      </div>
    )
  }


}